"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";

export interface NextStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: number; // 1-6
  onContinue: () => void; // Continue to next step
  onExit: () => void; // Exit wizard mode
  createdEntityName?: string; // e.g., "John's Plumbing" for the client name
}

interface StepContent {
  title: string;
  message: string;
  continueButtonText: string;
  exitButtonText: string;
  completedAction: string;
}

type StepKey = "client" | "quote" | "job" | "schedule" | "complete" | "invoice" | "default";

const stepKeyMap: Record<number, StepKey> = {
  1: "client",
  2: "quote",
  3: "job",
  4: "schedule",
  5: "complete",
  6: "invoice",
};

const getStepContent = (step: number, entityName: string | undefined, t: (key: string, params?: Record<string, string | number>) => string): StepContent => {
  const stepKey = stepKeyMap[step] || "default";

  if (step === 1 && entityName) {
    return {
      title: t(`workflow.steps.${stepKey}.title`),
      message: t(`workflow.steps.${stepKey}.message`, { name: entityName }),
      continueButtonText: t(`workflow.steps.${stepKey}.continueButton`),
      exitButtonText: t(`workflow.steps.${stepKey}.exitButton`),
      completedAction: t(`workflow.steps.${stepKey}.completedAction`),
    };
  }

  if (step === 1 && !entityName) {
    return {
      title: t(`workflow.steps.${stepKey}.title`),
      message: t(`workflow.steps.${stepKey}.messageNoName`),
      continueButtonText: t(`workflow.steps.${stepKey}.continueButton`),
      exitButtonText: t(`workflow.steps.${stepKey}.exitButton`),
      completedAction: t(`workflow.steps.${stepKey}.completedAction`),
    };
  }

  return {
    title: t(`workflow.steps.${stepKey}.title`),
    message: t(`workflow.steps.${stepKey}.message`),
    continueButtonText: t(`workflow.steps.${stepKey}.continueButton`),
    exitButtonText: t(`workflow.steps.${stepKey}.exitButton`),
    completedAction: t(`workflow.steps.${stepKey}.completedAction`),
  };
};

export function NextStepModal({
  isOpen,
  onClose,
  currentStep,
  onContinue,
  onExit,
  createdEntityName,
}: NextStepModalProps) {
  const t = useTranslations("workflow");
  const content = getStepContent(currentStep, createdEntityName, t);
  const isFinalStep = currentStep === 6;

  const handleContinue = () => {
    onContinue();
    onClose();
  };

  const handleExit = () => {
    onExit();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex flex-col items-center space-y-4 py-4">
            <div className="rounded-full bg-info/10 p-3 dark:bg-blue-950">
              <CheckCircle className="h-12 w-12 text-info dark:text-info" />
            </div>
            <DialogTitle className="text-2xl text-center">
              {content.title}
            </DialogTitle>
          </div>
          <DialogDescription className="text-center text-base pt-2">
            {content.message}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-center space-x-2 py-4">
          {[1, 2, 3, 4, 5, 6].map((step) => (
            <div
              key={step}
              className={`h-2 w-2 rounded-full transition-colors ${
                step <= currentStep
                  ? "bg-info dark:bg-blue-400"
                  : "bg-gray-300 dark:bg-gray-700"
              }`}
            />
          ))}
        </div>

        <div className="rounded-lg border border-info bg-info/10 p-4 dark:border-blue-900 dark:bg-blue-950/30">
          <p className="text-sm text-info dark:text-blue-300">
            <span className="font-semibold">{t("workflow.nextStep.whatsNext")}</span>{" "}
            {isFinalStep
              ? t("workflow.nextStep.finalStepInfo")
              : t("workflow.nextStep.continueInfo", { nextStep: currentStep + 1, totalSteps: 6 })}
          </p>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleExit}
            className="w-full sm:w-auto"
          >
            {content.exitButtonText}
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            className="w-full sm:w-auto bg-info hover:bg-blue-700 text-white dark:bg-info dark:hover:bg-blue-700"
          >
            {content.continueButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
